
import { useParams } from "react-router-dom";
import { useTestResults } from "@/hooks/useTestResults";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateAchievements } from "@/utils/testDataProcessing";
import { AchievementsCard } from "./overview/AchievementsCard";
import { NextTestCard } from "./overview/NextTestCard";
import { ProgressCard } from "./overview/ProgressCard";

export const OverviewSection = () => {
  const { id } = useParams();
  const { data: testResults, isLoading: isLoadingResults } = useTestResults(id || "");

  // Fetch child's age and available tests
  const { data: availableTests, isLoading: isLoadingTests } = useQuery({
    queryKey: ["availableTests", id],
    queryFn: async () => {
      if (!id) return [];

      // First get the child's age
      const { data: child } = await supabase
        .from("child_profiles")
        .select("age")
        .eq("id", id)
        .maybeSingle();

      if (!child) return [];

      console.log("Child age:", child.age);

      // Convert age to appropriate grade level format
      const ageLevel = child.age === 0 ? "Infant" : `Age ${child.age}`;
      console.log("Age level:", ageLevel);

      // Then fetch tests for that age level, ordered by category and stage
      const { data: tests, error } = await supabase
        .from("tests")
        .select("*")
        .eq("grade_level", ageLevel)
        .order('category', { ascending: true })
        .order('difficulty', { ascending: true });

      if (error) {
        console.error("Error fetching tests:", error);
        return [];
      }

      console.log("Available tests:", tests);
      return tests || [];
    },
    enabled: !!id,
  });

  // Filter test results to only keep the latest attempt for each test
  const latestTestResults = testResults?.reduce((acc: typeof testResults, result) => {
    const existingIndex = acc?.findIndex(r => r.test_id === result.test_id);
    if (existingIndex === -1) {
      acc?.push(result);
    } else if (acc && new Date(result.created_at) > new Date(acc[existingIndex].created_at)) {
      acc[existingIndex] = result;
    }
    return acc;
  }, [] as typeof testResults) || [];

  // Find the next untaken test based on new recommendation strategy
  const findNextTest = () => {
    if (!availableTests || !latestTestResults) return [];

    // Group tests by stage number
    const testsByStage: Record<number, any[]> = {};
    availableTests.forEach(test => {
      const stageMatch = test.title.match(/Stage (\d+)/i);
      const stage = stageMatch ? parseInt(stageMatch[1]) : 1;
      
      if (!testsByStage[stage]) {
        testsByStage[stage] = [];
      }
      testsByStage[stage].push(test);
    });

    // Get completed test IDs
    const completedTestIds = new Set(latestTestResults.map(result => result.test_id));

    // Start from Stage 1 and find tests that haven't been taken yet
    for (let stage = 1; stage <= Object.keys(testsByStage).length; stage++) {
      const testsInStage = testsByStage[stage] || [];
      
      // First, check for any untaken tests in this stage
      const untakenTests = testsInStage.filter(test => !completedTestIds.has(test.id));
      
      if (untakenTests.length > 0) {
        // Prioritize tests from categories where no test has been taken yet at this stage
        const categoriesWithCompletedTests = new Set(
          testsInStage
            .filter(test => completedTestIds.has(test.id))
            .map(test => test.category)
        );
        
        // Find tests from categories without any completed tests at this stage
        const testsFromNewCategories = untakenTests.filter(
          test => !categoriesWithCompletedTests.has(test.category)
        );
        
        if (testsFromNewCategories.length > 0) {
          // Return the first test from a new category
          return [testsFromNewCategories[0]];
        }
        
        // If all categories have at least one test completed or there are no categories with completed tests,
        // return the first untaken test
        return [untakenTests[0]];
      }
    }
    
    // If all tests have been taken, return empty array
    return [];
  };

  const nextTest = findNextTest();
  console.log("Next available test:", nextTest);

  // Process test results to show running average over time using latest attempts
  const chartData = latestTestResults
    ?.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .reduce((acc: { date: string; score: number }[], result, index, array) => {
      const resultsUpToNow = array.slice(0, index + 1);
      const totalScore = resultsUpToNow.reduce((sum, r) => sum + r.score, 0);
      const averageScore = Math.round(totalScore / resultsUpToNow.length);
      
      acc.push({
        date: new Date(result.created_at).toLocaleDateString(),
        score: averageScore
      });
      
      return acc;
    }, []) || [];

  // Generate achievements based on all test results (not just latest attempts)
  // to capture achievements in chronological order
  const achievements = testResults ? generateAchievements(testResults) : [];
  const earnedAchievements = achievements
    .filter(a => a.earned)
    .sort((a, b) => {
      if (!a.date || !b.date) return 0;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <AchievementsCard 
        isLoading={isLoadingResults}
        earnedAchievements={earnedAchievements}
      />
      <NextTestCard 
        isLoading={isLoadingTests}
        nextTest={nextTest}
      />
      <ProgressCard 
        isLoading={isLoadingResults}
        chartData={chartData}
      />
    </div>
  );
};
